import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Status and label pill. Tones follow the app's signal colours: success
 * (green), warning (amber), danger (red) are Tailwind defaults on purpose;
 * info uses the brand blues and neutral the blue-grey neutrals.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-chip font-medium', // squarer than buttons: information, not an action
  {
    variants: {
      tone: {
        neutral: 'bg-gray-100 text-gray-600',
        info: 'bg-blue-50 text-blue-700',
        success: 'bg-emerald-50 text-emerald-700',
        warning: 'bg-amber-50 text-amber-800',
        danger: 'bg-red-50 text-red-700',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  }
)

const dotTone = {
  neutral: 'bg-gray-400',
  info: 'bg-blue-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
} as const

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Show a coloured dot before the label. */
  dot?: boolean
}

export function Badge({ className, tone, size, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, size }), className)} {...props}>
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', dotTone[tone ?? 'neutral'])} aria-hidden />}
      {children}
    </span>
  )
}

export { badgeVariants }
